import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputBase,
  Button,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";
import { summaryTheme as t } from "./styles";
import { getComplianceIcon } from "../../assets/images/compliance/IconCompliance.jsx";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const DETAIL_TABLE_COLUMNS = [
  { id: "arn", label: "ARNs", desc: "Amazon Resource Name — unique identifier for the resource", width: "30%", minWidth: 220 },
  { id: "service", label: "Service", desc: "AWS service or product (e.g. S3, IAM, EC2)", width: "14%" },
  { id: "region", label: "Region", desc: "AWS region where the resource is located", width: "12%" },
  { id: "security_failed_checks", label: "Security", desc: "CSPM failed checks by severity for this ARN", width: "16%" },
  { id: "compliance_failed_total", label: "Compliance", desc: "Compliance status and control findings; hover for breakdown", width: "14%" },
  { id: "cloud_waste", label: "Cloud Waste", desc: "Cost Insight / cloud waste finding for this ARN", width: "7%" },
  { id: "cost_unblended_mtd", label: "Cost MTD", desc: "Unblended cost for this resource month-to-date (USD)", width: "7%" },
];

function formatCost(value) {
  if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
  const num = typeof value === "string" ? parseFloat(value, 10) : value;
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    const parts = String(dateString).split("-");
    if (parts.length >= 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
        return new Date(y, m, d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    }
    return String(dateString);
  } catch {
    return String(dateString);
  }
}

/** Shadcn-style tooltip: header + list (shown on hover) */
const complianceTooltipSx = {
  fontFamily: t.fontFamily,
  maxWidth: 320,
  padding: 0,
  borderRadius: t.radius.lg,
  border: `1px solid ${t.colors.border}`,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.02)",
  backgroundColor: t.colors.card,
  // Allow inner content (like scrollable lists) to render fully
  overflow: "visible",
};

/** Compliance cell: status chip or count; click opens modal in parent via onOpenDetails */
function ComplianceCell({ row, onOpenDetails }) {
  const remediations = Array.isArray(row.compliance_remediations)
    ? row.compliance_remediations
    : [];
  const hasRemediations = remediations.length > 0;
  const totalControls = Number(row.compliance_failed_total) || 0;
  const failedByFramework = row.compliance_failed_controls;
  const frameworksCount =
    failedByFramework && typeof failedByFramework === "object"
      ? Object.keys(failedByFramework).length
      : 0;

  const statusLower = (row.compliance_status || "").toLowerCase();
  if (statusLower === "unknown" || statusLower === "unknow") {
    return (
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontFamily: t.fontFamily,
          color: t.colors.textMuted,
        }}
      >
        —
      </Typography>
    );
  }

  const tooltipContent = hasRemediations ? (
    <Box sx={{ fontFamily: t.fontFamily, maxWidth: 360 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.borderLight,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: t.colors.text,
          }}
        >
          Compliance remediations
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: t.colors.textMuted,
            mt: 0.25,
          }}
        >
          {frameworksCount} framework{frameworksCount === 1 ? "" : "s"} ·{" "}
          {totalControls} failed control{totalControls === 1 ? "" : "s"} ·{" "}
          {remediations.length} remediation item
          {remediations.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 260, overflowY: "auto", py: 1 }}>
        {remediations.map((item, idx) => {
          const iconSrc = getComplianceIcon(item.framework);
          return (
            <Box
              key={`${item.framework || "fw"}-${idx}`}
              sx={{
                px: 2,
                py: 0.75,
                borderBottom: `1px solid ${t.colors.borderLight}`,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {iconSrc && (
                <Box
                  component="img"
                  src={iconSrc}
                  alt={item.framework}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 1,
                    objectFit: "contain",
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: t.colors.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.25,
                  }}
                >
                  {item.framework || "Framework"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: t.colors.text,
                    mb: 0.25,
                  }}
                >
                  {item.title}
                </Typography>
                {item.url && (
                  <Typography
                    component="a"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-block",
                      fontSize: "0.72rem",
                      color: t.colors.accent,
                      textDecoration: "none",
                      mb: 0.25,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View remediation
                  </Typography>
                )}
                {Array.isArray(item.control_ids) && item.control_ids.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: t.colors.textMuted,
                    }}
                  >
                    Controls: {item.control_ids.join(", ")}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  ) : null;

  const isUnknown =
    (row.compliance_status || "").toLowerCase() === "unknown" ||
    (row.compliance_status || "").toLowerCase() === "unknow";
  const chip = isUnknown ? (
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontFamily: t.fontFamily,
        color: t.colors.textMuted,
      }}
    >
      —
    </Typography>
  ) : (
    <StatusChip
      status={row.compliance_status}
      label={String(frameworksCount)}
    />
  );

  if (typeof onOpenDetails === "function") {
    return (
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenDetails(row);
        }}
        sx={{
          all: "unset",
          cursor: hasRemediations ? "pointer" : "default",
          display: "inline-flex",
        }}
      >
        {chip}
      </Box>
    );
  }

  if (tooltipContent) {
    return (
      <Tooltip
        title={tooltipContent}
        placement="top-start"
        enterDelay={200}
        leaveDelay={0}
        componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
      >
        <Box component="span" sx={{ display: "inline-block", cursor: "default" }}>
          {chip}
        </Box>
      </Tooltip>
    );
  }
  return chip;
}

function StatusChip({ status, label }) {
  const isFailed = (status || "").toLowerCase() === "failed";
  return (
    <Chip
      size="small"
      label={label ?? (isFailed ? "Failed" : "Passed")}
      sx={{
        fontWeight: 600,
        fontSize: "0.6875rem",
        fontFamily: t.fontFamily,
        borderRadius: t.radius.sm,
        height: 20,
        ...(isFailed
          ? { bgcolor: t.colors.destructiveBg, color: t.colors.destructive, border: `1px solid ${t.colors.destructiveBorder}` }
          : { bgcolor: t.colors.successBg, color: t.colors.success }),
      }}
    />
  );
}

const SECURITY_SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
const SECURITY_SEVERITY_COLORS = {
  CRITICAL: t.colors.severity.critical,
  HIGH: t.colors.severity.high,
  MEDIUM: t.colors.severity.medium,
  LOW: t.colors.severity.low,
};

/** Security column: show per-severity counts with remediation tooltip (using security_* fields) */
function SecurityCell({ row }) {
  const remediations = Array.isArray(row.security_remediations)
    ? row.security_remediations
    : [];
  const hasRemediations = remediations.length > 0;
  const totalChecks = Number(row.security_failed_checks) || 0;
  const bySeverity = row.security_failed_checks_by_severity || {};

  const tooltipContent = (
    <Box sx={{ fontFamily: t.fontFamily, maxWidth: 360 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.borderLight,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: t.colors.text,
          }}
        >
          Security remediations
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: t.colors.textMuted,
            mt: 0.25,
          }}
        >
          {totalChecks} failed check{totalChecks === 1 ? "" : "s"} ·{" "}
          {remediations.length} remediation item
          {remediations.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 260, overflowY: "auto", py: 1 }}>
        {remediations.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              px: 2,
              py: 0.75,
              borderBottom: `1px solid ${t.colors.borderLight}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 500,
                color: t.colors.text,
                mb: 0.25,
              }}
            >
              {item.title}
            </Typography>
            {item.url && (
              <Typography
                component="a"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-block",
                  fontSize: "0.72rem",
                  color: t.colors.accent,
                  textDecoration: "none",
                  mb: 0.25,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                View remediation
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {SECURITY_SEVERITY_ORDER.map((sev) => {
        const count =
          Number(
            bySeverity && typeof bySeverity === "object" ? bySeverity[sev] : 0
          ) || 0;
        if (count <= 0) return null;
        const label = sev.charAt(0) + sev.slice(1).toLowerCase(); // CRITICAL -> Critical
        return (
          <Typography
            key={sev}
            sx={{
              fontSize: "0.75rem",
              fontFamily: t.fontFamily,
              color: SECURITY_SEVERITY_COLORS[sev] || t.colors.text,
            }}
          >
            {label} ({count})
          </Typography>
        );
      })}
      {/* If no severities have counts, show a simple dash */}
      {(!bySeverity ||
        SECURITY_SEVERITY_ORDER.every((sev) => {
          const count =
            Number(
              bySeverity && typeof bySeverity === "object" ? bySeverity[sev] : 0
            ) || 0;
          return count <= 0;
        })) && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontFamily: t.fontFamily,
            color: t.colors.textMuted,
          }}
        >
          —
        </Typography>
      )}
    </Box>
  );

  if (!hasRemediations) {
    return content;
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement="top-start"
      enterDelay={200}
      leaveDelay={0}
      componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
    >
      <Box component="span" sx={{ display: "inline-block", cursor: "default" }}>
        {content}
      </Box>
    </Tooltip>
  );
}

/**
 * Account detail content: KPIs, service filter, resources table.
 * Used by Summary page (with accountNumber from dropdown) and by /summary/account/:id route.
 * When accounts/onAccountChange/getAccountLabel are provided, header shows only account dropdown (top right).
 */
export default function SummaryAccountDetailContent({
  accountNumber,
  showBackLink = false,
  accounts = [],
  accountsLoading = false,
  accountsError = null,
  onAccountChange,
  getAccountLabel,
  headerOnly = false,
  severity = "",
  onSeverityChange,
  framework = "",
  onFrameworkChange,
  frameworks = [],
}) {
  const history = useHistory();
  const [details, setDetails] = useState([]);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsPageSize, setDetailsPageSize] = useState(25);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [sortBy, setSortBy] = useState("arn");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedRow, setSelectedRow] = useState(null);
  const [complianceDialogRow, setComplianceDialogRow] = useState(null);
  const [complianceFrameworkFilter, setComplianceFrameworkFilter] = useState("");
  const [complianceControlFilter, setComplianceControlFilter] = useState("");

  const fetchDetails = useCallback(async (opts = {}) => {
    if (!accountNumber) return;
    const page = opts.page ?? detailsPage;
    const pageSize = opts.pageSize ?? detailsPageSize;
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      const params = { page, page_size: pageSize };
      if (selectedService) params.service = selectedService;
      if (severity) params.severity = severity;
      if (framework) params.framework = framework;
      const response = await get(
        `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/details`,
        { params }
      );
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const items = response?.items ?? [];
        setDetails(Array.isArray(items) ? items : []);
        setDetailsTotal(response?.total ?? items.length);
        setDetailsPage(page);
        setDetailsPageSize(pageSize);
      } else {
        setDetailsError(getApiErrorMessage(response) || "Failed to load details");
      }
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      setDetailsError(getApiErrorMessage(errData) || err?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  }, [accountNumber, detailsPage, detailsPageSize, selectedService, severity, framework]);

  const fetchServices = useCallback(async () => {
    if (!accountNumber) return;
    try {
      const response = await get(
        `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/services`
      );
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const list = response?.services ?? response?.items ?? (Array.isArray(response) ? response : []);
        setServicesList(Array.isArray(list) ? list : []);
      } else {
        setServicesList([]);
      }
    } catch {
      setServicesList([]);
    }
  }, [accountNumber]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleChangePage = (_, newPage) => setDetailsPage(newPage + 1);
  const handleChangeRowsPerPage = (e) => {
    setDetailsPageSize(parseInt(e.target.value, 10));
    setDetailsPage(1);
  };

  const filteredAndSortedDetails = useMemo(() => {
    let list = [...details];
    const q = (tableSearch || "").trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const arn = (row.arn || "").toLowerCase();
        const service = (row.service || "").toLowerCase();
        const region = (row.region || "").toLowerCase();
        const modules = Array.isArray(row.modules) ? row.modules.join(" ").toLowerCase() : "";
        return arn.includes(q) || service.includes(q) || region.includes(q) || modules.includes(q);
      });
    }
    list.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === "arn") {
        aVal = (a.arn || "").toLowerCase();
        bVal = (b.arn || "").toLowerCase();
      } else if (sortBy === "cost_unblended_mtd" || sortBy === "security_failed_checks" || sortBy === "compliance_failed_total" || sortBy === "cloud_waste") {
        aVal = Number(aVal) ?? 0;
        bVal = Number(bVal) ?? 0;
      } else if (sortBy === "snapshot_date") {
        aVal = a.snapshot_date || "";
        bVal = b.snapshot_date || "";
      } else {
        aVal = (aVal ?? "").toString().toLowerCase();
        bVal = (bVal ?? "").toString().toLowerCase();
      }
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [details, tableSearch, sortBy, sortOrder]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
  };

  const pageKpis = useMemo(() => {
    let securityFailed = 0;
    let complianceFailed = 0;
    let costMtd = 0;
    filteredAndSortedDetails.forEach((row) => {
      securityFailed += Number(row.security_failed_checks) || 0;
      complianceFailed += Number(row.compliance_failed_total) || 0;
      const c = row.cost_unblended_mtd;
      if (c != null && c !== "") costMtd += Number(c) || 0;
    });
    return { securityFailed, complianceFailed, costMtd };
  }, [filteredAndSortedDetails]);

  const complianceDialogRemediations = useMemo(() => {
    if (!complianceDialogRow || !Array.isArray(complianceDialogRow.compliance_remediations)) {
      return [];
    }
    const list = complianceDialogRow.compliance_remediations;
    const fw = (complianceFrameworkFilter || "").trim();
    const selectedControl = (complianceControlFilter || "").trim();

    return list.filter((item) => {
      const matchesFramework = fw ? item.framework === fw : true;
      if (!matchesFramework) return false;
      if (!selectedControl) return true;
      const controls = Array.isArray(item.control_ids) ? item.control_ids : [];
      return controls.includes(selectedControl);
    });
  }, [complianceDialogRow, complianceFrameworkFilter, complianceControlFilter]);

  const renderHeader = () => {
    if (headerOnly && typeof onAccountChange === "function") {
      return (
        <FormControl
          size="small"
          fullWidth
          sx={{
            minWidth: 220,
            "& .MuiOutlinedInput-root": {
              fontFamily: t.fontFamily,
              fontSize: "0.875rem",
              backgroundColor: t.colors.card,
              borderRadius: t.radius.md,
              "& fieldset": { borderColor: t.colors.border },
              "&:hover fieldset": { borderColor: t.colors.textMuted },
              "&.Mui-focused fieldset": { borderColor: t.colors.accent, borderWidth: "1px" },
            },
          }}
        >
          <InputLabel id="detail-account-label" sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>
            View account
          </InputLabel>
          <Select
            labelId="detail-account-label"
            id="detail-account-select"
            value={accountNumber || ""}
            label="View account"
            onChange={(e) => onAccountChange(e.target.value)}
            sx={{ fontFamily: t.fontFamily, fontWeight: 500, "& .MuiSelect-select": { py: 1.25 } }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1.5,
                  borderRadius: t.radius.md,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  maxHeight: 320,
                  "& .MuiMenuItem-root": { fontFamily: t.fontFamily, fontSize: "0.875rem" },
                },
              },
            }}
          >
            {accountsLoading ? (
              <MenuItem value="" disabled>Loading…</MenuItem>
            ) : accountsError ? (
              <MenuItem value="" disabled>{accountsError}</MenuItem>
            ) : (
              accounts.map((acc, i) => {
                const num = acc?.account_number ?? acc?.accountNumber ?? acc?.id ?? "";
                if (!num) return null;
                return (
                  <MenuItem key={num || i} value={num}>
                    {typeof getAccountLabel === "function" ? getAccountLabel(acc) : num}
                  </MenuItem>
                );
              })
            )}
            {!accountsLoading && !accountsError && accounts.length === 0 && (
              <MenuItem value="" disabled>No accounts</MenuItem>
            )}
          </Select>
        </FormControl>
      );
    }

    if (showBackLink) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography
            component="span"
            onClick={() => history.push("/summary")}
            sx={{
              color: t.colors.accent,
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": { color: t.colors.accentHover, textDecoration: "underline" },
            }}
          >
            ← Back to ARNs Insight
          </Typography>
        </Box>
      );
    }

    return null;
  };

  const handleOpenComplianceDetails = (row) => {
    setComplianceDialogRow(row);
    setComplianceFrameworkFilter("");
    setComplianceControlFilter("");
  };

  const handleCloseComplianceDetails = () => {
    setComplianceDialogRow(null);
  };

  if (!accountNumber) {
    return (
      <Box sx={{ fontFamily: t.fontFamily }}>
        {renderHeader()}
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Typography sx={{ color: t.colors.textMuted, fontSize: "0.875rem" }}>
            {headerOnly
              ? "Select an account from the dropdown above to view details."
              : "Select an account to view details."}
          </Typography>
        </Box>
      </Box>
    );
  }

  const tableCellSx = {
    fontFamily: t.fontFamily,
    fontSize: "0.8125rem",
    borderBottom: `1px solid ${t.colors.border}`,
    py: 1,
    px: 1.75,
  };
  const headerSx = {
    ...tableCellSx,
    backgroundColor: t.colors.borderLight,
    fontWeight: 600,
    color: t.colors.textMuted,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  return (
    <Box sx={{ fontFamily: t.fontFamily }}>
      {renderHeader()}

      {servicesList.length > 0 && (
        <Box sx={{ borderRadius: t.radius.lg, backgroundColor: t.colors.card, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow, p: 2, mb: 3 }}>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: t.colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5 }}>Filter by service</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Chip label="All" size="small" onClick={() => { setSelectedService(""); setDetailsPage(1); }} sx={{ fontFamily: t.fontFamily, fontWeight: 500, fontSize: "0.75rem", borderRadius: t.radius.sm, bgcolor: !selectedService ? t.colors.accent : t.colors.borderLight, color: !selectedService ? "#fff" : t.colors.text, cursor: "pointer", "&:hover": { bgcolor: !selectedService ? t.colors.accentHover : "#e4e4e7" } }} />
            {servicesList.map((item, i) => {
              const name = item?.service ?? item?.name ?? String(i);
              const total = item?.total ?? 0;
              return (
                <Chip key={i} label={`${name}: ${total}`} size="small" onClick={() => { setSelectedService(selectedService === name ? "" : name); setDetailsPage(1); }} sx={{ fontFamily: t.fontFamily, fontWeight: 500, fontSize: "0.75rem", borderRadius: t.radius.sm, bgcolor: selectedService === name ? t.colors.accent : t.colors.borderLight, color: selectedService === name ? "#fff" : t.colors.text, cursor: "pointer", "&:hover": { bgcolor: selectedService === name ? t.colors.accentHover : "#e4e4e7" } }} />
              );
            })}
          </Box>
        </Box>
      )}

      {detailsError && (
        <Box sx={{ p: 2, mb: 2, borderRadius: t.radius.md, bgcolor: t.colors.destructiveBg, border: `1px solid ${t.colors.destructiveBorder}` }}>
          <Typography sx={{ fontSize: "0.875rem", color: t.colors.destructive, fontFamily: t.fontFamily }}>{detailsError}</Typography>
        </Box>
      )}

      {detailsLoading && (
        <Box sx={{ display: "flex", alignItems: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: t.colors.accent }} />
          <Typography sx={{ ml: 2, color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}>Loading ARNs...</Typography>
        </Box>
      )}

      {!detailsLoading && !detailsError && (
        <Box sx={{ backgroundColor: t.colors.card, borderRadius: t.radius.lg, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow, overflow: "hidden" }}>
          {/* Client-side search + severity filter for table only */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${t.colors.border}`,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
              <SearchIcon sx={{ color: t.colors.textMuted, fontSize: 18 }} />
              <InputBase
                placeholder="Search by ARN, service, region, modules..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  maxWidth: 360,
                  fontSize: "0.8125rem",
                  fontFamily: t.fontFamily,
                  "& input": { py: 0.5 },
                }}
              />
              {tableSearch && (
                <Typography
                  component="span"
                  sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                >
                  {filteredAndSortedDetails.length} of {details.length} on this page
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component="span"
                  sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                >
                  Severity:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={severity || ""}
                    onChange={(e) => {
                      const val = e.target.value || "";
                      if (typeof onSeverityChange === "function") {
                        onSeverityChange(val);
                      }
                    }}
                    displayEmpty
                    sx={{
                      fontSize: "0.8125rem",
                      height: 32,
                      fontFamily: t.fontFamily,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="CRITICAL">Critical</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {Array.isArray(frameworks) && frameworks.length > 0 && typeof onFrameworkChange === "function" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                  >
                    Framework:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={framework || ""}
                      onChange={(e) => {
                        const val = e.target.value || "";
                        onFrameworkChange(val);
                      }}
                      displayEmpty
                      sx={{
                        fontSize: "0.8125rem",
                        height: 32,
                        fontFamily: t.fontFamily,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                      }}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {frameworks.map((fw, idx) => {
                        const name = fw?.framework || fw?.name || fw || "";
                        if (!name) return null;
                        const iconSrc = getComplianceIcon(name);
                        return (
                          <MenuItem key={`${name}-${idx}`} value={name}>
                            {iconSrc && (
                              <Box
                                component="img"
                                src={iconSrc}
                                alt={name}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 1,
                                  objectFit: "contain",
                                  mr: 1,
                                }}
                              />
                            )}
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: "50vh" }}>
            <Table stickyHeader size="medium" sx={{ minWidth: 900, fontFamily: t.fontFamily }}>
              <TableHead>
                <TableRow>
                  {DETAIL_TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        ...headerSx,
                        width: col.width,
                        minWidth: col.minWidth,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() => handleSort(col.id)}
                    >
                      <Tooltip title={col.desc || ""} placement="top" arrow>
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                          {col.label}
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: t.colors.textMuted, ml: 0.25 }} />
                          {sortBy === col.id && (sortOrder === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={DETAIL_TABLE_COLUMNS.length} sx={{ ...tableCellSx, py: 6, textAlign: "center", color: t.colors.textMuted }}>
                      {tableSearch ? "No rows match your search on this page." : "No ARNs"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedDetails.map((row, i) => (
                    <TableRow
                      key={row.arn ?? i}
                      selected={selectedRow?.arn === row.arn}
                      onClick={() => setSelectedRow(selectedRow?.arn === row.arn ? null : row)}
                      sx={{
                        "& td": tableCellSx,
                        "&:hover": { bgcolor: t.colors.background },
                        cursor: "pointer",
                      }}
                    >
                      <TableCell sx={{ fontFamily: t.fontFamily, minWidth: 220, maxWidth: 420 }}>
                        <Tooltip title={row.arn || ""} placement="top-start">
                          <Typography component="span" sx={{ fontSize: "0.8125rem", fontFamily: "monospace", color: t.colors.text, display: "block", whiteSpace: "normal", wordBreak: "break-all" }}>
                            {row.arn || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily, color: t.colors.text, fontWeight: 500 }}>{row.service ?? "—"}</TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily, color: t.colors.textMuted }}>{row.region ?? "—"}</TableCell>
                      <TableCell>
                        <SecurityCell row={row} />
                      </TableCell>
                      <TableCell>
                        <ComplianceCell
                          row={row}
                          onOpenDetails={handleOpenComplianceDetails}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily }}>
                        {row.cloud_waste === true
                          ? "True"
                          : row.cloud_waste === false
                          ? "False"
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily, fontVariantNumeric: "tabular-nums" }}>
                        {formatCost(row.cost_unblended_mtd)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Summary-style footer: Showing X–Y of Z, rows per page, Previous/Next */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `1px solid ${t.colors.border}`,
              backgroundColor: t.colors.card,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              borderRadius: "0 0 12px 12px",
            }}
          >
            <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.8125rem", fontWeight: 400, fontFamily: t.fontFamily }}>
              Showing {detailsTotal === 0 ? 0 : (detailsPage - 1) * detailsPageSize + 1}–
              {Math.min(detailsPage * detailsPageSize, detailsTotal)} of {detailsTotal}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", fontFamily: t.fontFamily }}>
                Rows per page
              </Typography>
              <FormControl size="small" sx={{ minWidth: 56 }}>
                <Select
                  value={detailsPageSize}
                  onChange={(e) => {
                    setDetailsPageSize(Number(e.target.value));
                    setDetailsPage(1);
                  }}
                  sx={{
                    fontSize: "0.8125rem",
                    height: 32,
                    fontFamily: t.fontFamily,
                    color: t.colors.text,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n} sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage <= 1}
                onClick={() => setDetailsPage((p) => p - 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage * detailsPageSize >= detailsTotal}
                onClick={() => setDetailsPage((p) => p + 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog
        open={!!complianceDialogRow}
        onClose={handleCloseComplianceDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: t.radius.lg,
            boxShadow:
              "0 24px 48px rgba(15,23,42,0.25), 0 0 0 1px rgba(15,23,42,0.04)",
            border: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: t.fontFamily,
            fontSize: "0.9375rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: t.fontFamily,
                fontSize: "0.9375rem",
                fontWeight: 600,
              }}
            >
              Compliance details
            </Typography>
            {complianceDialogRow?.arn && (
              <Typography
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: t.colors.textMuted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={complianceDialogRow.arn}
              >
                {complianceDialogRow.arn}
              </Typography>
            )}
          </Box>
          <IconButton
            edge="end"
            aria-label="Close"
            onClick={handleCloseComplianceDetails}
            sx={{
              ml: 1,
              color: t.colors.textMuted,
              "&:hover": { color: t.colors.text },
            }}
          >
            {/* Close icon kept simple to match shadcn-style dialogs */}
            <span style={{ fontSize: 18 }}>×</span>
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 2,
            pt: 2.25,
            pb: 2.5,
            backgroundColor: t.colors.background,
          }}
        >
          {complianceDialogRow && (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  mt: 0.75,
                  mb: 1.75,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel
                    sx={{
                      fontFamily: t.fontFamily,
                      fontSize: "0.78rem",
                    }}
                  >
                    Framework
                  </InputLabel>
                  <Select
                    label="Framework"
                    value={complianceFrameworkFilter}
                    onChange={(e) => setComplianceFrameworkFilter(e.target.value)}
                    sx={{
                      fontFamily: t.fontFamily,
                      fontSize: "0.8rem",
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {Array.isArray(complianceDialogRow.compliance_remediations) &&
                      Array.from(
                        new Set(
                          complianceDialogRow.compliance_remediations
                            .map((r) => r.framework)
                            .filter(Boolean)
                        )
                      ).map((fw) => {
                        const iconSrc = getComplianceIcon(fw);
                        return (
                          <MenuItem key={fw} value={fw}>
                            {iconSrc && (
                              <Box
                                component="img"
                                src={iconSrc}
                                alt={fw}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 1,
                                  objectFit: "contain",
                                  mr: 1,
                                }}
                              />
                            )}
                            {fw}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel
                    sx={{
                      fontFamily: t.fontFamily,
                      fontSize: "0.78rem",
                    }}
                  >
                    Control ID
                  </InputLabel>
                  <Select
                    label="Control ID"
                    value={complianceControlFilter}
                    onChange={(e) => setComplianceControlFilter(e.target.value)}
                    sx={{
                      fontFamily: t.fontFamily,
                      fontSize: "0.8rem",
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {Array.isArray(complianceDialogRow.compliance_remediations) &&
                      Array.from(
                        new Set(
                          complianceDialogRow.compliance_remediations
                            .flatMap((r) =>
                              Array.isArray(r.control_ids) ? r.control_ids : []
                            )
                            .filter(Boolean)
                        )
                      ).map((cid) => (
                        <MenuItem key={cid} value={cid}>
                          {cid}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
              <TableContainer
                sx={{
                  maxHeight: 320,
                  borderRadius: t.radius.md,
                  border: `1px solid ${t.colors.border}`,
                  backgroundColor: t.colors.card,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontFamily: t.fontFamily,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          backgroundColor: t.colors.borderLight,
                          py: 1,
                          px: 1.5,
                          borderBottom: `1px solid ${t.colors.border}`,
                          width: "45%",
                        }}
                      >
                        Framework
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: t.fontFamily,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          backgroundColor: t.colors.borderLight,
                          py: 1,
                          px: 1.5,
                          borderBottom: `1px solid ${t.colors.border}`,
                          width: "25%",
                        }}
                      >
                        Control IDs
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: t.fontFamily,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          backgroundColor: t.colors.borderLight,
                          py: 1,
                          px: 1.5,
                          borderBottom: `1px solid ${t.colors.border}`,
                          width: "30%",
                        }}
                      >
                        Remediation
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {complianceDialogRemediations.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            fontFamily: t.fontFamily,
                            fontSize: "0.8rem",
                            color: t.colors.textMuted,
                            textAlign: "center",
                            py: 3,
                          }}
                        >
                          No remediations match the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      complianceDialogRemediations.map((item, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            "&:last-of-type td": {
                              borderBottom: "none",
                            },
                            "& td": {
                              borderBottom: `1px solid ${t.colors.borderLight}`,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: t.fontFamily,
                              fontSize: "0.78rem",
                              py: 1,
                              px: 1.5,
                            }}
                          >
                            {item.framework || "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: t.fontFamily,
                              fontSize: "0.78rem",
                              py: 1,
                              px: 1.5,
                            }}
                          >
                            {Array.isArray(item.control_ids) &&
                            item.control_ids.length > 0
                              ? item.control_ids.join(", ")
                              : "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: t.fontFamily,
                              fontSize: "0.78rem",
                              py: 1,
                              px: 1.5,
                            }}
                          >
                            {item.url ? (
                              <Typography
                                component="a"
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  fontFamily: t.fontFamily,
                                  fontSize: "0.78rem",
                                  color: t.colors.accent,
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                View remediation
                              </Typography>
                            ) : (
                              item.title || "—"
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseComplianceDetails}
            sx={{
              textTransform: "none",
              fontFamily: t.fontFamily,
              fontSize: "0.8rem",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
